import { Trans, useLingui } from '@lingui/react/macro';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { selectPopupIsLoaded, selectPopupLoadCount } from 'modules/overlay-v2/store/overlay/selectors';
import { selectTime } from 'modules/overlay-v2/store/time/selectors';
import useGameInfo from 'modules/overlay-v2/ui/hooks/use-game-info';
import useOverlayRouteParams from 'modules/overlay-v2/ui/hooks/use-overlay-route-params';
import usePopupMessenger from 'modules/overlay-v2/ui/hooks/use-popup-messenger';
import useTimer from 'modules/overlay-v2/ui/hooks/use-timer';
import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import UDButton from 'ud-ui/components/button';
import UDText from 'ud-ui/components/text';

export interface PopupBoard {
  id: number;
  name: string;
}

interface PopupControlProps {
  /** Табло, для каждого из которых панель открывает своё окно, — в порядке шаблона. */
  boards: PopupBoard[];
  isLocked?: boolean;
  isHidePanel?: boolean;
}

/**
 * Кнопки окон оверлея. Одно табло — одна кнопка «Открыть окно», как раньше; несколько —
 * по строке на табло с его названием. «Синхронизировать окна» одна на все: команды
 * и так уходят во все открытые окна (`PopupRegistryProvider`).
 */
function PopupControl(props: PopupControlProps) {
  const { boards, isLocked, isHidePanel } = props;
  const { organizerId, deviceId, token } = useOverlayRouteParams();
  const { t, i18n } = useLingui();
  const lang = i18n.locale;

  const { hasPopups, send, registerPopup } = usePopupMessenger();
  const popupIsLoaded = useSelector(selectPopupIsLoaded);
  const popupLoadCount = useSelector(selectPopupLoadCount);
  const gameInfo = useGameInfo();
  const time = useSelector(selectTime);
  const { timer } = useTimer();

  const handleOpenOverlay = useCallback(
    (boardId: number) => {
      if (organizerId === undefined || deviceId === undefined || !token) return;

      const windowProps = `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, width=1200, height=800`;
      // Окно оверлея открывается для того же устройства, что и панель: матч оно возьмёт
      // из трансляции само, поэтому в ссылке остаются только табло, режим показа и язык пульта.
      const path = Pages.OVERLAY_V2.builders.index(String(organizerId), String(deviceId), token, {
        boardId,
        locked: isLocked,
        hidePanel: isHidePanel,
        lang,
      });
      // Имя окна включает табло: у каждого табло своё окно, повторное нажатие переоткрывает его же.
      const popup = window.open(window.location.origin + path, `Overlay window ${boardId}`, windowProps);
      registerPopup(boardId, popup);
    },
    [deviceId, isHidePanel, isLocked, lang, organizerId, registerPopup, token],
  );

  const handleSync = useCallback(() => {
    if (!popupIsLoaded) return;
    send({ type: 'syncState', gameInfo, time, timer });
  }, [popupIsLoaded, send, gameInfo, time, timer]);

  // Каждое загрузившееся окно — и второе TV, и перезагруженное — получает состояние пульта:
  // ручные правки счёта и фолов живут только здесь, с сервера окно их не возьмёт.
  useEffect(() => {
    handleSync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [send, popupLoadCount]);

  const syncButton = (
    <UDButton onClick={handleSync} disabled={!hasPopups || !popupIsLoaded} variant={'dark'} stretch>
      <Trans>Синхронизировать окна</Trans>
    </UDButton>
  );

  if (boards.length === 1) {
    return (
      <div className={'flex flex-row gap-4'}>
        <UDButton onClick={() => handleOpenOverlay(boards[0].id)} variant={'primary'} stretch>
          <Trans>Открыть окно</Trans>
        </UDButton>
        {syncButton}
      </div>
    );
  }

  return (
    <div className={'flex flex-col gap-2'}>
      {boards.map((board) => {
        const boardName = board.name;
        return (
          <div key={board.id} className={'flex flex-row items-center gap-4'}>
            <UDText type={'subhead'} color={'white'} className={'min-w-0 flex-1 truncate'} title={boardName}>
              {boardName}
            </UDText>
            <UDButton onClick={() => handleOpenOverlay(board.id)} variant={'primary'} aria-label={t`Открыть окно «${boardName}»`}>
              <Trans>Открыть окно</Trans>
            </UDButton>
          </div>
        );
      })}
      {syncButton}
    </div>
  );
}

export default PopupControl;
